import React, { useEffect, useMemo, useState } from 'react';
import { VStack, HStack, Text, Input, Button } from '@chakra-ui/react';
import Drawer, { DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter, DrawerCloseTrigger } from '@/components/ui/Drawer';
import withHashDrawer from '@/components/ui/withHashDrawer';
import { useChangeProfileSlugMutation, useGetUserByIdQuery } from '../api';

interface ChangeProfileSlugModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId?: number;
  onSuccess?: () => void;
}

const getApiErrorMessage = (err: any): string => {
  const slugError = err?.data?.errors?.slug?.[0];

  if (slugError) {
    return slugError;
  }

  return err?.data?.message || 'Failed to change profile URL';
};

const ChangeProfileSlugModal: React.FC<ChangeProfileSlugModalProps> = ({
  isOpen,
  onClose,
  entityId,
  onSuccess,
}) => {
  const [slug, setSlug] = useState('');
  const [error, setError] = useState('');
  const { data: user } = useGetUserByIdQuery(entityId!, {
    skip: !entityId,
  });
  const [changeProfileSlug, { isLoading }] = useChangeProfileSlugMutation();

  const currentSlug = useMemo(() => user?.profile?.slug || user?.slug || '', [user]);

  useEffect(() => {
    if (isOpen) {
      setSlug(currentSlug);
      setError('');
    }
  }, [currentSlug, isOpen]);

  const handleSubmit = async () => {
    if (!user?.profile?.id) {
      setError('User profile is required');
      return;
    }

    const nextSlug = slug.trim();
    setError('');

    if (!nextSlug) {
      setError('UNIQUE-URL-0001');
      return;
    }

    if (nextSlug.length < 5) {
      setError('UNIQUE-URL-0002');
      return;
    }

    if (nextSlug.length > 20) {
      setError('UNIQUE-URL-0003');
      return;
    }

    if (/\s/.test(nextSlug)) {
      setError('Slug cannot contain spaces');
      return;
    }

    try {
      await changeProfileSlug({
        profileId: user.profile.id,
        slug: nextSlug,
      }).unwrap();

      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(getApiErrorMessage(err));
    }
  };

  const handleClose = () => {
    setSlug('');
    setError('');
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} size="md">
      <DrawerHeader>
        <DrawerTitle>Change URL</DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>

      <DrawerBody>
        <VStack gap={4} align="stretch">
          {user && (
            <Text fontSize="sm" color="gray.600">
              Changing profile URL for: <strong>{user.first_name} {user.last_name}</strong>
            </Text>
          )}

          <div>
            <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={1}>
              Current URL Slug
            </Text>
            <Text>{currentSlug || 'N/A'}</Text>
          </div>

          <div>
            <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={1}>
              New URL Slug <Text as="span" color="red.500">*</Text>
            </Text>
            <Input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="dr-john-smith"
            />
            {error && (
              <Text color="red.500" fontSize="sm" mt={1}>
                {error}
              </Text>
            )}
          </div>

          <Text fontSize="sm" color="gray.600">
            Slug must be 5 to 20 characters and unique.
          </Text>
        </VStack>
      </DrawerBody>

      <DrawerFooter>
        <HStack gap={3}>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={isLoading}
            disabled={isLoading}
            colorPalette="blue"
          >
            Change URL
          </Button>
        </HStack>
      </DrawerFooter>
    </Drawer>
  );
};

export default withHashDrawer(ChangeProfileSlugModal, {
  action: 'change-slug',
});
